/*
 * Charm builder: pick a base, add letters and charms, see them on the product photo,
 * then add every piece to the cart as normal products tagged with one design code.
 */
(function () {
  if (customElements.get('charm-builder')) return;

  const cutoutCache = new Map();

  // Remove the white studio background from a piece photo and trim it to the piece itself.
  function cutout(url) {
    if (cutoutCache.has(url)) return cutoutCache.get(url);
    const job = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          resolve(processCutout(img));
        } catch (e) {
          resolve({ src: url, aspect: 1, raw: true });
        }
      };
      img.onerror = () => resolve({ src: url, aspect: 1, raw: true });
      img.src = url;
    });
    cutoutCache.set(url, job);
    return job;
  }

  function processCutout(img) {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    const data = ctx.getImageData(0, 0, w, h);
    const px = data.data;
    const bg = new Uint8Array(w * h);

    const isWhite = (i) => {
      const r = px[i * 4], g = px[i * 4 + 1], b = px[i * 4 + 2];
      const min = Math.min(r, g, b), max = Math.max(r, g, b);
      return min > 228 && max - min < 22;
    };

    // Flood fill the background from every edge pixel.
    const stack = [];
    for (let x = 0; x < w; x++) stack.push(x, (h - 1) * w + x);
    for (let y = 0; y < h; y++) stack.push(y * w, y * w + w - 1);
    while (stack.length) {
      const i = stack.pop();
      if (bg[i] || !isWhite(i)) continue;
      bg[i] = 1;
      const x = i % w, y = (i / w) | 0;
      if (x > 0) stack.push(i - 1);
      if (x < w - 1) stack.push(i + 1);
      if (y > 0) stack.push(i - w);
      if (y < h - 1) stack.push(i + w);
    }

    // Small enclosed white gaps (inside A, B, D, O...) are background too.
    const seen = new Uint8Array(w * h);
    const holeLimit = w * h * 0.015;
    for (let start = 0; start < w * h; start++) {
      if (bg[start] || seen[start] || !isWhite(start)) continue;
      const region = [];
      const q = [start];
      seen[start] = 1;
      let pure = 0;
      while (q.length) {
        const i = q.pop();
        region.push(i);
        if (Math.min(px[i * 4], px[i * 4 + 1], px[i * 4 + 2]) > 242) pure++;
        const x = i % w, y = (i / w) | 0;
        const n = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
        for (const j of n) {
          if (j >= 0 && !seen[j] && !bg[j] && isWhite(j)) {
            seen[j] = 1;
            q.push(j);
          }
        }
      }
      if (region.length <= holeLimit && pure / region.length > 0.8) {
        for (const i of region) bg[i] = 1;
      }
    }

    // Drop dust and specks: keep only the piece itself (the biggest blob and anything of similar size).
    const label = new Int32Array(w * h).fill(-1);
    const sizes = [];
    for (let start = 0; start < w * h; start++) {
      if (bg[start] || label[start] >= 0) continue;
      const id = sizes.length;
      let size = 0;
      const q = [start];
      label[start] = id;
      while (q.length) {
        const i = q.pop();
        size++;
        const x = i % w, y = (i / w) | 0;
        const n = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
        for (const j of n) {
          if (j >= 0 && !bg[j] && label[j] < 0) {
            label[j] = id;
            q.push(j);
          }
        }
      }
      sizes.push(size);
    }
    const keepMin = Math.max(...sizes, 0) * 0.05;
    for (let i = 0; i < w * h; i++) {
      if (!bg[i] && sizes[label[i]] < keepMin) bg[i] = 1;
    }

    let minX = w, minY = h, maxX = -1, maxY = -1;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        if (bg[i]) {
          px[i * 4 + 3] = 0;
          continue;
        }
        // Soften the light fringe next to the background.
        const touchesBg = (x > 0 && bg[i - 1]) || (x < w - 1 && bg[i + 1]) || (y > 0 && bg[i - w]) || (y < h - 1 && bg[i + w]);
        if (touchesBg) {
          const min = Math.min(px[i * 4], px[i * 4 + 1], px[i * 4 + 2]);
          px[i * 4 + 3] = Math.max(90, Math.min(255, (255 - min) * 6));
        }
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) throw new Error('empty');
    ctx.putImageData(data, 0, 0);

    const cw = maxX - minX + 1;
    const ch = maxY - minY + 1;
    const out = document.createElement('canvas');
    out.width = cw;
    out.height = ch;
    out.getContext('2d').drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
    return { src: out.toDataURL('image/png'), aspect: cw / ch };
  }

  const colourCache = new Map();

  // The product's colour from its photo. Sampled from the front of the band (lower part of the
  // loop, away from the buckle) so leather linings and buckles don't skew it; most common colour wins.
  function sampleColour(url) {
    if (colourCache.has(url)) return colourCache.get(url);
    const job = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const size = 120;
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = size;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, size, size);
          const px = ctx.getImageData(0, 0, size, size).data;
          const isBg = (i) => {
            const min = Math.min(px[i], px[i + 1], px[i + 2]);
            return min > 215 && Math.max(px[i], px[i + 1], px[i + 2]) - min < 25;
          };

          let x0 = size, x1 = -1, y0 = size, y1 = -1;
          for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
              if (isBg((y * size + x) * 4)) continue;
              x0 = Math.min(x0, x); x1 = Math.max(x1, x);
              y0 = Math.min(y0, y); y1 = Math.max(y1, y);
            }
          }
          if (x1 < 0) return resolve(null);

          const bins = new Map();
          const fromX = x0 + Math.floor((x1 - x0) * 0.3), toX = x1 - Math.floor((x1 - x0) * 0.05);
          const fromY = y0 + Math.floor((y1 - y0) * 0.65);
          for (let y = fromY; y <= y1; y++) {
            for (let x = fromX; x < toX; x++) {
              const i = (y * size + x) * 4;
              if (isBg(i)) continue;
              const key = (px[i] >> 5) * 64 + (px[i + 1] >> 5) * 8 + (px[i + 2] >> 5);
              const bin = bins.get(key) || { n: 0, r: 0, g: 0, b: 0 };
              bin.n++; bin.r += px[i]; bin.g += px[i + 1]; bin.b += px[i + 2];
              bins.set(key, bin);
            }
          }
          let best = null;
          bins.forEach((bin) => { if (!best || bin.n > best.n) best = bin; });
          if (!best) return resolve(null);
          resolve(`rgb(${Math.round(best.r / best.n)}, ${Math.round(best.g / best.n)}, ${Math.round(best.b / best.n)})`);
        } catch (e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
    colourCache.set(url, job);
    return job;
  }

  // Trim the white space around the design, keeping a small margin and a 2:1 shape.
  function cropToDesign(canvas) {
    const { width: w, height: h } = canvas;
    const px = canvas.getContext('2d').getImageData(0, 0, w, h).data;
    let x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (let y = 0; y < h; y += 2) {
      for (let x = 0; x < w; x += 2) {
        const i = (y * w + x) * 4;
        if (Math.min(px[i], px[i + 1], px[i + 2]) > 240) continue;
        x0 = Math.min(x0, x); x1 = Math.max(x1, x);
        y0 = Math.min(y0, y); y1 = Math.max(y1, y);
      }
    }
    if (x1 < 0) return canvas;
    const pad = 20;
    let cw = x1 - x0 + pad * 2;
    let ch = Math.max(y1 - y0 + pad * 2, cw / 2);
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    const out = document.createElement('canvas');
    out.width = Math.round(cw);
    out.height = Math.round(ch);
    const ctx = out.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, cx - cw / 2, cy - ch / 2, cw, ch, 0, 0, cw, ch);
    return out;
  }

  function randomCode(length, alphabet) {
    const bytes = crypto.getRandomValues(new Uint8Array(length));
    return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
  }

  class CharmBuilder extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-builder-data]').textContent);
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: this.data.currency || 'GBP',
      });
      this.state = { base: 0, variant: 0, letterSet: 0, pieces: [], selected: -1, ownBase: false };
      this.$ = (sel) => this.querySelector(sel);

      const firstAvailable = (variants) => Math.max(0, variants.findIndex((v) => v.available));
      if (this.data.bases.length) this.state.variant = firstAvailable(this.data.bases[0].variants);
      const startPiece = this.applyLink();

      this.renderBases();
      this.renderLetterSets();
      this.renderKeys();
      this.renderCharms('');
      this.bindEvents();
      this.update();
      if (startPiece) this.addPiece(startPiece);
    }

    // Links from product pages: ?base=<handle>&variant=<variant id> picks the keychain/bracelet and colour,
    // ?letters=<handle> picks the letter colour, ?piece=<variant id> starts the design with that letter or charm.
    applyLink() {
      const params = new URLSearchParams(window.location.search);
      const baseIndex = this.data.bases.findIndex((b) => b.handle === params.get('base'));
      if (baseIndex >= 0) {
        this.state.base = baseIndex;
        const v = this.data.bases[baseIndex].variants.findIndex((x) => String(x.id) === params.get('variant'));
        this.state.variant = v >= 0 ? v : Math.max(0, this.data.bases[baseIndex].variants.findIndex((x) => x.available));
      }
      const letterSet = this.data.letterSets.findIndex((l) => l.handle === params.get('letters'));
      if (letterSet >= 0) this.state.letterSet = letterSet;
      const pieceId = params.get('piece');
      if (!pieceId) return null;
      for (let i = 0; i < this.data.letterSets.length; i++) {
        const v = this.data.letterSets[i].variants.find((x) => String(x.id) === pieceId);
        if (v && v.available) {
          this.state.letterSet = i;
          return { kind: 'letter', id: v.id, label: v.title, colour: this.letterColour(this.data.letterSets[i]), image: v.image, price: v.price };
        }
      }
      const c = this.data.charms.find((x) => String(x.id) === pieceId);
      return c && c.available ? { kind: 'charm', id: c.id, label: c.title, image: c.image, price: c.price } : null;
    }

    get base() {
      return this.data.bases[this.state.base];
    }

    get variant() {
      return this.base && this.base.variants[this.state.variant];
    }

    get letterSet() {
      return this.data.letterSets[this.state.letterSet];
    }

    letterColour(set) {
      return set.title.replace(/^.*letters\s+in\s+/i, '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    }

    // ---------- rendering of choices ----------

    renderBases() {
      const wrap = this.$('[data-bases]');
      wrap.innerHTML = '';
      this.data.bases.forEach((base, i) => {
        const v = base.variants[0];
        const b = this.choice('charm-builder__base-card', i === this.state.base);
        b.innerHTML = `<span class="charm-builder__zoom"><img src="${v.thumb}" alt="" loading="lazy" width="400" height="400" style="transform: scale(${base.zoom})"></span><span>${escapeHtml(base.title)}</span><span class="charm-builder__price">${this.money.format(v.price / 100)}</span>`;
        b.addEventListener('click', () => {
          this.state.base = i;
          this.state.variant = Math.max(0, base.variants.findIndex((x) => x.available));
          this.trimToCapacity();
          this.renderBases();
          this.update();
        });
        wrap.appendChild(b);
      });
      this.renderColours();
    }

    renderColours() {
      const wrap = this.$('[data-colours]');
      wrap.innerHTML = '';
      if (!this.base) return;
      this.$('[data-colours-label]').hidden = false;
      this.base.variants.forEach((v, i) => {
        const b = this.choice('charm-builder__swatch', i === this.state.variant);
        b.disabled = !v.available;
        b.title = v.available ? v.title : `${v.title} (sold out)`;
        b.innerHTML = `<span class="charm-builder__zoom"><img src="${v.thumb}" alt="" loading="lazy" width="400" height="400" style="transform: scale(${this.base.zoom})"></span><span>${escapeHtml(v.title)}</span>`;
        b.addEventListener('click', () => {
          this.state.variant = i;
          this.renderColours();
          this.update();
        });
        wrap.appendChild(b);
      });
      this.$('[data-colour-name]').textContent = this.variant ? this.variant.title : '';
    }

    renderLetterSets() {
      const wrap = this.$('[data-letter-colours]');
      wrap.innerHTML = '';
      this.data.letterSets.forEach((set, i) => {
        const sample = set.variants.find((v) => v.title === 'A') || set.variants[0];
        const b = this.choice('charm-builder__swatch', i === this.state.letterSet);
        const name = this.letterColour(set);
        b.title = name;
        b.innerHTML = `<span class="charm-builder__zoom"><img src="${sample.image}" alt="" loading="lazy" width="80" height="80"></span><span>${escapeHtml(name)}</span>`;
        b.addEventListener('click', () => {
          this.state.letterSet = i;
          this.renderLetterSets();
          this.renderKeys();
        });
        wrap.appendChild(b);
      });
      this.$('[data-letter-colour-name]').textContent = this.letterSet ? this.letterColour(this.letterSet) : '';
    }

    renderKeys() {
      const wrap = this.$('[data-keys]');
      wrap.innerHTML = '';
      if (!this.letterSet) return;
      this.letterSet.variants.forEach((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'charm-builder__key';
        b.textContent = v.title;
        b.disabled = !v.available;
        b.setAttribute('aria-label', v.available ? `Add letter ${v.title}` : `Letter ${v.title} sold out`);
        b.addEventListener('click', () => this.addLetter(v));
        wrap.appendChild(b);
      });
    }

    renderCharms(query) {
      const wrap = this.$('[data-charms]');
      const q = query.trim().toLowerCase();
      wrap.innerHTML = '';
      const list = this.data.charms.filter((c) => !q || `${c.title} ${c.group}`.toLowerCase().includes(q));
      if (!list.length) {
        wrap.innerHTML = '<p class="charm-builder__none">No charms match that search.</p>';
        return;
      }
      list.forEach((c) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'charm-builder__charm';
        b.disabled = !c.available;
        b.innerHTML = `<span class="charm-builder__zoom"><img src="${c.image}" alt="" loading="lazy" width="150" height="150"></span><span>${escapeHtml(c.title)}</span><span class="charm-builder__price">${c.available ? this.money.format(c.price / 100) : 'Sold out'}</span>`;
        b.setAttribute('aria-label', `Add ${c.title} charm`);
        b.addEventListener('click', () => this.addPiece({ kind: 'charm', id: c.id, label: c.title, image: c.image, price: c.price }));
        wrap.appendChild(b);
      });
    }

    choice(className, selected) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = className;
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', selected ? 'true' : 'false');
      return b;
    }

    // ---------- editing the design ----------

    get capacity() {
      return this.base ? this.base.max : 0;
    }

    addLetter(v) {
      return this.addPiece({
        kind: 'letter',
        id: v.id,
        label: v.title,
        colour: this.letterColour(this.letterSet),
        image: v.image,
        price: v.price,
      });
    }

    addPiece(piece) {
      if (this.state.pieces.length >= this.capacity) {
        this.showError(`This ${this.base.noun} fits up to ${this.capacity} letters and charms.`);
        return false;
      }
      this.showError('');
      const at = this.state.selected >= 0 ? this.state.selected + 1 : this.state.pieces.length;
      this.state.pieces.splice(at, 0, piece);
      if (this.state.selected >= 0) this.state.selected = at;
      this.update();
      return true;
    }

    typeName(text) {
      const missing = [];
      for (const ch of text.toUpperCase()) {
        if (ch === ' ') continue;
        const v = this.letterSet && this.letterSet.variants.find((x) => x.title.toUpperCase() === ch);
        if (!v || !v.available) {
          missing.push(ch);
          continue;
        }
        if (!this.addLetter(v)) return;
      }
      if (missing.length) {
        this.showError(`Not available in ${this.letterColour(this.letterSet)}: ${[...new Set(missing)].join(' ')}`);
      }
    }

    trimToCapacity() {
      if (this.state.pieces.length > this.capacity) {
        this.state.pieces.length = this.capacity;
        this.state.selected = -1;
        this.showError(`This ${this.base.noun} fits up to ${this.capacity} pieces, so we removed the extra ones.`);
      }
    }

    bindEvents() {
      this.$('[data-type-form]').addEventListener('submit', (e) => {
        e.preventDefault();
        const input = this.$('[data-type-input]');
        this.typeName(input.value);
        input.value = '';
      });
      this.$('[data-charm-search]').addEventListener('input', (e) => this.renderCharms(e.target.value));
      this.$('[data-clear]').addEventListener('click', () => {
        this.state.pieces = [];
        this.state.selected = -1;
        this.showError('');
        this.update();
      });
      this.querySelectorAll('[data-move]').forEach((b) =>
        b.addEventListener('click', () => {
          const i = this.state.selected;
          const j = i + Number(b.dataset.move);
          if (i < 0 || j < 0 || j >= this.state.pieces.length) return;
          const p = this.state.pieces;
          [p[i], p[j]] = [p[j], p[i]];
          this.state.selected = j;
          this.update();
        })
      );
      this.$('[data-remove]').addEventListener('click', () => {
        if (this.state.selected < 0) return;
        this.state.pieces.splice(this.state.selected, 1);
        this.state.selected = -1;
        this.showError('');
        this.update();
      });
      this.$('[data-add]').addEventListener('click', () => this.addToCart());
      this.$('[data-own-base]').addEventListener('change', (e) => {
        this.state.ownBase = e.target.checked;
        this.renderSummary();
      });
    }

    // ---------- preview + summary ----------

    update() {
      const variant = this.variant;
      const img = this.$('[data-base-image]');
      this.$('[data-stage-empty]').hidden = !!variant;
      this.querySelectorAll('[data-noun]').forEach((el) => (el.textContent = this.base ? this.base.noun : ''));
      const band = !!variant && this.base.display === 'band';
      this.$('[data-band-layer]').hidden = !band;
      img.hidden = band;
      const thumb = this.$('[data-thumb]');
      this.$('[data-thumb-wrap]').hidden = !band;
      if (this.base) thumb.style.transform = `scale(${this.base.zoom})`;
      if (variant && img.dataset.src !== variant.image) {
        img.dataset.src = variant.image;
        img.src = variant.image;
        img.alt = `${this.base.title} in ${variant.title}`;
        thumb.src = variant.thumb;
        thumb.alt = `${this.base.title} in ${variant.title}`;
      }
      if (band) this.drawBand(variant);
      this.$('[data-colour-name]').textContent = variant ? variant.title : '';
      this.renderChips();
      this.renderPieces();
      this.renderSummary();
    }

    // Bracelets etc. whose photos aren't flat: draw a straight band in the product's colour.
    async drawBand(variant) {
      const el = this.$('[data-band]');
      const [left, right, centreY, pieceHeight] = this.base.area;
      // Real bracelet straps are about half as tall as the letters, which slide over them.
      const h = pieceHeight * 0.5;
      el.dataset.texture = this.base.texture;
      Object.assign(el.style, {
        left: `${left - 9}%`,
        width: `${right - left + 24}%`,
        top: `${centreY - h / 2}%`,
        height: `${h}%`,
      });
      el.dataset.rainbow = /rainbow/i.test(variant.title) ? 'true' : 'false';
      const colour = await sampleColour(variant.thumb);
      if (this.variant === variant) el.style.setProperty('--band-colour', colour || '#444');
    }

    renderChips() {
      const list = this.$('[data-chips]');
      const pieces = this.state.pieces;
      list.innerHTML = '';
      pieces.forEach((p, i) => {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'charm-builder__chip';
        b.setAttribute('aria-pressed', i === this.state.selected ? 'true' : 'false');
        b.innerHTML = `<span class="charm-builder__zoom"><img src="${p.image}" alt="" width="40" height="40"></span><span>${escapeHtml(p.label)}</span>`;
        b.title = p.kind === 'letter' ? `${p.label} (${p.colour})` : p.label;
        b.addEventListener('click', () => {
          this.state.selected = this.state.selected === i ? -1 : i;
          this.renderChips();
        });
        li.appendChild(b);
        list.appendChild(li);
      });
      this.$('[data-chip-tools]').hidden = this.state.selected < 0;
      this.renderChipColours();
      this.$('[data-clear]').hidden = !pieces.length;
      this.$('[data-count]').textContent = this.base
        ? `${pieces.length} of ${this.capacity} pieces${pieces.length ? ' · tap a piece to move, remove or recolour it' : ''}`
        : '';
    }

    // When a letter is selected, tap a colour to change just that letter.
    renderChipColours() {
      const wrap = this.$('[data-chip-colours]');
      const i = this.state.selected;
      const p = this.state.pieces[i];
      wrap.innerHTML = '';
      wrap.hidden = !p || p.kind !== 'letter';
      if (wrap.hidden) return;
      const label = document.createElement('p');
      label.className = 'charm-builder__chip-colours-label';
      label.textContent = `Colour of ${p.label}:`;
      wrap.appendChild(label);
      this.data.letterSets.forEach((set) => {
        const v = set.variants.find((x) => x.title === p.label);
        const name = this.letterColour(set);
        const b = this.choice('charm-builder__swatch', name === p.colour);
        b.disabled = !v || !v.available;
        b.title = v && v.available ? name : `${name} (sold out)`;
        b.innerHTML = `${v ? `<span class="charm-builder__zoom"><img alt="" width="80" height="80"></span>` : ''}<span>${escapeHtml(name)}</span>`;
        // The photos are framed differently: show each letter cut out of its photo so they're all the same size.
        if (v) cutout(v.image).then((c) => (b.querySelector('img').src = c.src));
        b.addEventListener('click', () => {
          this.state.pieces[i] = { ...p, id: v.id, colour: name, image: v.image, price: v.price };
          this.update();
        });
        wrap.appendChild(b);
      });
    }

    async renderPieces() {
      const layer = this.$('[data-pieces]');
      const token = (this.renderToken = (this.renderToken || 0) + 1);
      const pieces = this.state.pieces.slice();
      const cut = await Promise.all(pieces.map((p) => cutout(p.image)));
      if (token !== this.renderToken) return;

      layer.innerHTML = '';
      if (!this.base || !pieces.length) return;
      this.layout(cut).forEach((box, i) => {
        const el = document.createElement('img');
        el.src = cut[i].src;
        el.alt = '';
        el.className = 'charm-builder__piece' + (cut[i].raw ? ' charm-builder__piece--raw' : '');
        el.style.left = `${box.x}%`;
        el.style.top = `${box.y}%`;
        el.style.width = `${box.w}%`;
        el.style.height = `${box.h}%`;
        layer.appendChild(el);
      });
    }

    // Where each piece sits, in % of the square product photo.
    layout(cut) {
      const [left, right, centreY, pieceHeight] = this.base.area;
      const gap = pieceHeight * 0.06;
      const widths = cut.map((c) => pieceHeight * c.aspect);
      const total = widths.reduce((a, b) => a + b, 0) + gap * (widths.length - 1);
      const scale = Math.min(1, (right - left) / total);
      const h = pieceHeight * scale;
      let x = left + (right - left - total * scale) / 2;
      return widths.map((w) => {
        const box = { x, y: centreY - h / 2, w: w * scale, h };
        x += (w + gap) * scale;
        return box;
      });
    }

    // A picture of the finished design for the basket: the middle band of the square photo, 2:1.
    async snapshot() {
      const W = 1000;
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = W / 2;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, W, W / 2);
      const toPx = (pct) => (pct / 100) * W;
      const toY = (pct) => toPx(pct) - W / 4;
      const load = (src) =>
        new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });

      if (this.base.display === 'band') {
        const [left, right, centreY, pieceHeight] = this.base.area;
        const h = pieceHeight * 0.5;
        const x = toPx(left - 9), y = toY(centreY - h / 2), w = toPx(right - left + 24), bh = toPx(h);
        ctx.fillStyle = (await sampleColour(this.variant.thumb)) || '#444';
        ctx.beginPath();
        ctx.roundRect(x, y, w, bh, bh / 2);
        ctx.fill();
        ctx.strokeStyle = '#c4c4c4';
        ctx.lineWidth = 5;
        ctx.strokeRect(x + w * 0.025, y - bh * 0.4, w * 0.04, bh * 1.8);
      } else {
        const img = await load(this.variant.image);
        ctx.drawImage(img, 0, img.naturalHeight / 4, img.naturalWidth, img.naturalHeight / 2, 0, 0, W, W / 2);
      }

      const cut = await Promise.all(this.state.pieces.map((p) => cutout(p.image)));
      const imgs = await Promise.all(cut.map((c) => load(c.src)));
      this.layout(cut).forEach((box, i) => ctx.drawImage(imgs[i], toPx(box.x), toY(box.y), toPx(box.w), toPx(box.h)));
      return cropToDesign(canvas).toDataURL('image/jpeg', 0.85);
    }

    // Upload the picture so it can be linked from the order (Supabase storage: insert-only, public read).
    // Returns its link, or null if storage isn't set up or doesn't answer quickly. Never blocks the order.
    async uploadPicture(code, src) {
      const { pictureStorageUrl: base, pictureStorageKey: key } = this.dataset;
      if (!base || !key) return null;
      try {
        const name = `${code}-${randomCode(8, 'abcdefghijklmnopqrstuvwxyz0123456789')}.jpg`;
        const blob = await (await fetch(src)).blob();
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        const res = await fetch(`${base}/storage/v1/object/designs/${name}`, {
          method: 'POST',
          headers: { apikey: key, Authorization: `Bearer ${key}`, 'Content-Type': 'image/jpeg' },
          body: blob,
          signal: controller.signal,
        });
        clearTimeout(timer);
        return res.ok ? `${base}/storage/v1/object/public/designs/${name}` : null;
      } catch (e) {
        return null;
      }
    }

    // The basket page shows this picture on the base line (see charm-builder-cart.js).
    savePreview(code, src) {
      try {
        const key = 'mb-designs';
        const saved = JSON.parse(localStorage.getItem(key) || '[]').filter((d) => d.code !== code).slice(-9);
        saved.push({ code, src });
        localStorage.setItem(key, JSON.stringify(saved));
      } catch (e) {
        // No picture in the basket is fine; the order still lists every piece.
      }
    }

    renderSummary() {
      const lines = this.$('[data-lines]');
      lines.innerHTML = '';
      let total = 0;
      const add = (label, price) => {
        total += price;
        const li = document.createElement('li');
        li.innerHTML = `<span>${escapeHtml(label)}</span><span>${this.money.format(price / 100)}</span>`;
        lines.appendChild(li);
      };
      if (this.variant && !this.state.ownBase) add(`${this.base.title} – ${this.variant.title}`, this.variant.price);
      const letters = this.state.pieces.filter((p) => p.kind === 'letter');
      const charms = this.state.pieces.filter((p) => p.kind === 'charm');
      if (letters.length) add(`${letters.length} letter${letters.length > 1 ? 's' : ''}`, letters.reduce((a, p) => a + p.price, 0));
      if (charms.length) add(`${charms.length} charm${charms.length > 1 ? 's' : ''}`, charms.reduce((a, p) => a + p.price, 0));
      this.$('[data-total]').textContent = this.money.format(total / 100);
      this.$('[data-add]').disabled = !this.variant || (!this.variant.available && !this.state.ownBase) || !this.state.pieces.length;
      if (this.base) this.$('[data-own-base-noun]').textContent = this.base.noun;
    }

    showError(msg) {
      const el = this.$('[data-error]');
      el.textContent = msg;
      el.hidden = !msg;
    }

    // ---------- cart ----------

    async addToCart() {
      const button = this.$('[data-add]');
      const code = 'MB-' + randomCode(5, 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789');
      const order = this.state.pieces.map((p) => (p.kind === 'letter' ? `${p.label} (${p.colour})` : p.label)).join(' · ');
      const designProps = { 'Design code': code, 'Pieces in order': order };
      const items = [];
      if (this.state.ownBase) {
        designProps['Goes on customer’s own'] = `${this.base.title} – ${this.variant.title} (not included)`;
      } else {
        items.push({ id: this.variant.id, quantity: 1, properties: designProps });
      }
      const counts = new Map();
      this.state.pieces.forEach((p) => counts.set(p.id, (counts.get(p.id) || 0) + 1));
      // Shopify lists the newest line first, so send the pieces last-to-first to show them in design order.
      [...counts].reverse().forEach(([id, quantity]) => items.push({ id, quantity, properties: { 'Design code': code } }));
      // Without a base, the design details ride on the first piece's line (which Shopify lists first).
      if (this.state.ownBase) items[items.length - 1].properties = designProps;

      button.disabled = true;
      button.classList.add('loading');
      this.showError('');
      let picture = null;
      try {
        picture = await this.snapshot();
      } catch (e) {
        // No picture: the order still lists every piece in order.
      }
      if (picture) {
        const link = await this.uploadPicture(code, picture);
        if (link) designProps['_Design picture'] = link;
      }
      try {
        const res = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.description || body.message || 'Could not add to basket');
        if (picture) this.savePreview(code, picture);
        window.location.href = this.dataset.cartUrl;
      } catch (e) {
        this.showError(`${e.message}. Please try again.`);
        button.disabled = false;
        button.classList.remove('loading');
      }
    }
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  }

  customElements.define('charm-builder', CharmBuilder);
})();
